<template>
    <div class="dashboard-body-futuristic">
        <!-- Main Container matches Dashboard -->
        <main class="futuristic-container">

            <!-- Reusing the EXACT SAME sidebar component -->
            <AppSidebar :items="accountMenuItems" :active-item="activeTab" :back-button="backButtonConfig"
                @item-click="handleSidebarClick" />

            <section class="dashboard-card-futuristic dashboard-card-scrollable">

                <!-- PROFILE SECTION -->
                <!-- PROFILE SECTION -->
                <div v-if="activeTab === 'profile'" class="account-section">

                    <div class="section-header">
                        <h2 class="section-title">My Profile</h2>
                        <p class="section-subtitle">Manage your account settings and preferences.</p>
                    </div>

                    <div class="profile-card">
                        <div class="card-body">

                            <!-- Left: Avatar & Stats -->
                            <div class="avatar-section">
                                <div class="avatar-wrapper">
                                    <img src="@/assets/user_icon.png" alt="Profile" class="avatar-image">


                                    <div class="avatar-upload-btn">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <path
                                                d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z">
                                            </path>
                                            <circle cx="12" cy="13" r="4"></circle>
                                        </svg>
                                    </div>
                                </div>
                                <h3 class="user-meta-name">{{ userProfile.name || 'User' }}</h3>


                                <!-- Profile Strength Widget -->
                                <div class="profile-strength">
                                    <div class="strength-header">
                                        <span>Profile Strength</span>
                                        <span class="strength-score">{{ profileStrength }}%</span>
                                    </div>
                                    <div class="strength-bar-bg">
                                        <div class="strength-bar-fill" :style="{ width: profileStrength + '%' }"></div>
                                    </div>
                                    <p class="strength-hint">{{ profileHint }}</p>
                                </div>
                            </div>

                            <!-- Right: Form -->
                            <form @submit.prevent="safeUpdateProfile" class="form-grid">

                                <div class="form-group">
                                    <label>Full Name</label>
                                    <div class="input-wrapper">
                                        <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                        <input type="text" v-model="userProfile.name" class="form-input with-icon"
                                            placeholder="Enter your full name" required>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <div class="label-row">
                                        <label>Email Address</label>
                                        <span class="badge-verified">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="3">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            Verified
                                        </span>
                                    </div>
                                    <div class="input-wrapper disabled-overlay">
                                        <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <path
                                                d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z">
                                            </path>
                                            <polyline points="22,6 12,13 2,6"></polyline>
                                        </svg>
                                        <input type="email" v-model="userProfile.email" class="form-input with-icon"
                                            placeholder="Enter your email" readonly>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label>Mobile Number</label>
                                    <div class="mobile-row">
                                        <!-- Country Code Picker -->
                                        <div class="cc-picker" v-click-outside="closeCountryDropdown">
                                            <button type="button" class="cc-trigger" :disabled="otpVerified" @click="toggleCountryDropdown">
                                                <span class="cc-flag">{{ selectedCountry.flag }}</span>
                                                <span class="cc-code">+{{ countryCode }}</span>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                                                    :style="{ transform: ccDropdownOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }">
                                                    <polyline points="6 9 12 15 18 9" />
                                                </svg>
                                            </button>
                                            <div v-if="ccDropdownOpen" class="cc-dropdown">
                                                <div class="cc-search-wrap">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2">
                                                        <circle cx="11" cy="11" r="8" />
                                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                    </svg>
                                                    <input ref="ccSearchInput" v-model="ccSearch" type="text" class="cc-search"
                                                        placeholder="Search country..." @click.stop />
                                                </div>
                                                <ul class="cc-list">
                                                    <li v-for="c in filteredCountryCodes" :key="c.code + c.name" class="cc-item"
                                                        :class="{ active: countryCode === c.code }" @click="selectCountry(c)">
                                                        <span class="cc-item-flag">{{ c.flag }}</span>
                                                        <span class="cc-item-name">{{ c.name }}</span>
                                                        <span class="cc-item-code">+{{ c.code }}</span>
                                                    </li>
                                                    <li v-if="filteredCountryCodes.length === 0" class="cc-empty">No results</li>
                                                </ul>
                                            </div>
                                        </div>

                                        <!-- Mobile Input with Verified Badge -->
                                        <div class="mobile-input-wrapper">
                                            <input type="tel" v-model.trim="userProfile.mobile"
                                                @input="userProfile.mobile = userProfile.mobile.replace(/[^0-9]/g, ''); resetOtpState();"
                                                maxlength="15" inputmode="numeric" autocomplete="off" placeholder="Mobile number"
                                                class="form-input with-icon" :class="{ 'error-input': mobileError || (otpSent && errors.otp), 'verified': otpVerified }" :disabled="otpVerified" />
                                            <!-- Verified Checkmark Badge -->
                                            <Transition name="badge-pop">
                                                <div v-if="otpVerified" class="verified-badge-circle">
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3.5"
                                                        stroke-linecap="round" stroke-linejoin="round">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                </div>
                                            </Transition>
                                        </div>

                                        <!-- Send OTP / Change Button -->
                                        <button v-if="!otpVerified" type="button" class="otp-send-btn"
                                            :disabled="otpSending || userProfile.mobile.length < 7 || resendTimer > 0 || resendCount >= maxResends"
                                            @click="sendOtp"
                                            :title="resendCount >= maxResends ? `Max ${maxResends} resends reached. Wait 10 min.` : ''">
                                            <span v-if="otpSending" class="spinner-sm"></span>
                                            <span v-else-if="otpSent && resendTimer > 0">{{ resendTimer }}s</span>
                                            <span v-else-if="otpSent && resendCount >= maxResends" style="font-size: 0.75rem;">Max resends</span>
                                            <span v-else-if="otpSent">Resend ({{ resendCount }}/{{ maxResends }})</span>
                                            <span v-else>Send OTP</span>
                                        </button>

                                        <!-- Change Button (when verified) -->
                                        <button v-else type="button" class="change-btn" @click="resetOtpState" title="Change mobile number">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <path d="M1 4v6h6M23 20v-6h-6" />
                                                <path d="M20.49 9A9 9 0 0 0 5.64 5.64M3.51 15A9 9 0 0 0 18.36 18.36" />
                                            </svg>
                                        </button>
                                    </div>
                                    <p v-if="mobileError" class="error-text"
                                        style="font-size: 0.8rem; margin-top: 4px; color: #ef4444;">{{ mobileError }}
                                    </p>

                                    <!-- OTP Input Section -->
                                    <div v-if="otpSent && !otpVerified" style="margin-top: 16px;">
                                        <p class="otp-hint">
                                            OTP sent to +{{ countryCode }}{{ userProfile.mobile }}. Valid for 10 minutes.
                                            <span v-if="otpAttempts > 0 && !otpLocked" style="margin-left: 8px; color: #ea580c;">
                                                {{ maxOtpAttempts - otpAttempts }} attempt(s) remaining
                                            </span>
                                        </p>
                                        <div class="otp-boxes-row">
                                            <input v-for="i in 6" :key="i" :ref="el => { if (el) otpRefs[i - 1] = el }" type="text"
                                                inputmode="numeric" maxlength="1" class="otp-box" :class="{ 'error-input': errors.otp || otpLocked }"
                                                :value="otpDigits[i - 1]" :disabled="otpLocked" @input="onOtpDigitInput(i - 1, $event)"
                                                @keydown="onOtpKeydown(i - 1, $event)" @paste="onOtpPaste($event)" />
                                            <button type="button" class="otp-send-btn" style="background:#16a34a;"
                                                :disabled="otpVerifying || otpDigits.join('').length !== 6 || otpLocked" @click="verifyOtp">
                                                <span v-if="otpVerifying" class="spinner-sm"></span>
                                                <span v-else>Verify</span>
                                            </button>
                                        </div>
                                        <p v-if="otpLocked" class="error-msg" style="color: #dc2626; font-weight: 600; margin-top: 8px;">
                                            Maximum attempts reached. Please request a new OTP.
                                        </p>
                                        <p v-else-if="errors.otp" class="error-msg" style="margin-top: 8px;">{{ errors.otp }}</p>
                                    </div>
                                </div>


                                <div class="form-actions">
                                    <button type="submit" class="btn-primary" :disabled="!!mobileError || isSaving">
                                        <svg v-if="!isSaving" width="18" height="18" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z">
                                            </path>
                                            <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                            <polyline points="7 3 7 8 15 8"></polyline>
                                        </svg>
                                        <span v-else>Saving...</span>
                                        {{ isSaving ? '' : 'Save Changes' }}
                                    </button>
                                </div>
                            </form>

                        </div>
                    </div>
                </div>

                <!-- MY PLAN / SUBSCRIPTION SECTION -->
                <div v-if="activeTab === 'subscription'" class="account-section">
                    <div class="section-header">
                        <h2 class="section-title">My Plan</h2>
                        <p class="section-subtitle">Your company's subscription and usage this month.</p>
                    </div>

                    <div v-if="myPlanLoading" class="plan-loading-state">Loading your plan…</div>

                    <div v-else-if="!myPlan" class="profile-card">
                        <div class="plan-empty-state">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            <p>No subscription plan is assigned to your company yet.</p>
                            <span class="plan-empty-hint">Contact your company administrator to get a plan assigned.</span>
                        </div>
                    </div>

                    <div v-else class="profile-card plan-card-account">
                        <div class="plan-card-top">
                            <div>
                                <h3 class="plan-name-account">{{ myPlan.plan_name }}</h3>
                                <div class="plan-price-row">
                                    <span class="plan-price-amount">₹{{ (myPlan.price || 0).toLocaleString('en-IN') }}</span>
                                    <span class="plan-price-period">/ {{ planDurationLabel }}</span>
                                </div>
                            </div>
                            <span class="plan-status-pill" :class="planStatusClass">{{ planStatusLabel }}</span>
                        </div>

                        <p v-if="myPlan.plan_expiry_date" class="plan-expiry-line">
                            Valid until <strong>{{ formatPlanDate(myPlan.plan_expiry_date) }}</strong>
                            <template v-if="myPlan.days_until_expiry !== null && myPlan.days_until_expiry >= 0">
                                ({{ myPlan.days_until_expiry }} day{{ myPlan.days_until_expiry === 1 ? '' : 's' }} left)
                            </template>
                        </p>

                        <div class="plan-usage-grid">
                            <div class="plan-usage-item">
                                <div class="plan-usage-head">
                                    <span>PDFs This Month</span>
                                    <span class="plan-usage-count">
                                        {{ myPlan.pdfs_used_this_month }}<template v-if="myPlan.max_pdfs_per_month !== null">/{{ myPlan.max_pdfs_per_month }}</template>
                                    </span>
                                </div>
                                <div v-if="myPlan.max_pdfs_per_month !== null" class="plan-usage-bar-bg">
                                    <div class="plan-usage-bar-fill" :class="{ warn: pdfUsagePct >= 90 }" :style="{ width: pdfUsagePct + '%' }"></div>
                                </div>
                                <span v-else class="plan-usage-unlimited">Unlimited</span>
                            </div>

                            <div class="plan-usage-item">
                                <div class="plan-usage-head">
                                    <span>Licenses Generated</span>
                                    <span class="plan-usage-count">
                                        {{ myPlan.licenses_generated }}<template v-if="myPlan.max_licenses !== null">/{{ myPlan.max_licenses }}</template>
                                    </span>
                                </div>
                                <div v-if="myPlan.max_licenses !== null" class="plan-usage-bar-bg">
                                    <div class="plan-usage-bar-fill" :class="{ warn: licenseGeneratedPct >= 90 }" :style="{ width: licenseGeneratedPct + '%' }"></div>
                                </div>
                                <span v-else class="plan-usage-unlimited">Unlimited</span>
                            </div>

                            <div class="plan-usage-item">
                                <div class="plan-usage-head">
                                    <span>Licenses Used</span>
                                    <span class="plan-usage-count">
                                        {{ myPlan.licenses_used }}<template v-if="myPlan.licenses_generated">/{{ myPlan.licenses_generated }}</template>
                                    </span>
                                </div>
                                <div class="plan-usage-bar-bg">
                                    <div class="plan-usage-bar-fill" :style="{ width: licenseUsedPct + '%' }"></div>
                                </div>
                            </div>
                        </div>

                        <div class="plan-modules-row">
                            <span class="plan-modules-label">Included Modules</span>
                            <div class="plan-modules-chips">
                                <span v-for="m in myPlan.enabled_modules" :key="m" class="plan-module-chip">{{ moduleLabel(m) }}</span>
                                <span v-if="!myPlan.enabled_modules || !myPlan.enabled_modules.length" class="plan-module-chip none">No modules enabled</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- RESET PASSWORD SECTION -->
                <!-- RESET PASSWORD SECTION -->
                <div v-if="activeTab === 'password'" class="account-section">
                    <div class="section-header">
                        <h2 class="section-title">Reset Password</h2>
                        <p class="section-subtitle">Manage your security preferences</p>
                    </div>

                    <div class="profile-card split-layout">
                        <!-- Left: Visual Side -->
                        <div class="split-visual">
                            <div class="visual-content">
                                <div class="visual-icon-circle">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                    </svg>
                                </div>
                                <h3>Secure Your Account</h3>
                                <p>A strong password helps prevent unauthorized access to your account.</p>
                            </div>
                            <!-- Decorative Circles -->
                            <div class="circle-1"></div>
                            <div class="circle-2"></div>
                        </div>

                        <!-- Right: Form Side -->
                        <div class="split-form">
                            <form @submit.prevent="handlePasswordReset" class="form-grid">

                                <div class="form-group">
                                    <label>Current Password</label>
                                    <div class="input-wrapper">
                                        <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <path
                                                d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4">
                                            </path>
                                        </svg>
                                        <input type="password" v-model="passwordForm.current"
                                            class="form-input with-icon" required placeholder="Enter current password">
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label>New Password</label>
                                    <div class="input-wrapper">
                                        <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                        </svg>
                                        <input type="password" v-model="passwordForm.new" class="form-input with-icon"
                                            placeholder="Enter new password" @input="validatePasswordStrength">
                                    </div>
                                    <!-- Strength Meter -->
                                    <div v-if="passwordForm.new" class="strength-meter">
                                        <div class="strength-bar">
                                            <div class="strength-fill"
                                                :style="{ width: strengthPercent + '%', backgroundColor: strengthColor }">
                                            </div>
                                        </div>
                                        <span class="strength-text" :style="{ color: strengthColor }">{{ strengthLabel
                                            }}</span>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label>Confirm Password</label>
                                    <div class="input-wrapper">
                                        <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                        </svg>
                                        <input type="password" v-model="passwordForm.confirm"
                                            class="form-input with-icon" placeholder="Confirm new password">
                                    </div>
                                </div>

                                <div class="status-messages">
                                    <p v-if="errorMsg" class="error-text">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="8" x2="12" y2="12"></line>
                                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                        </svg>
                                        {{ errorMsg }}
                                    </p>
                                    <p v-if="successMsg" class="success-text">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                        </svg>
                                        {{ successMsg }}
                                    </p>
                                </div>

                                <div class="form-actions">
                                    <button type="submit" class="btn-primary">
                                        Update Password
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

            </section>

        </main>
    </div>
</template>

<script>
import AppSidebar from './Sidebar.vue';
import axios from 'axios';
import { notify } from '@/utils/notify';


export default {
    name: 'AccountPage',
    components: {
        AppSidebar
    },
    props: {
        getAccessToken: Function
    },
    data() {
        return {
            activeTab: (this.$route.query.tab === 'reset_password' ? 'password'
                : this.$route.query.tab === 'subscription' ? 'subscription' : 'profile'),
            userProfile: {
                name: '',
                email: '',
                mobile: ''
            },
            myPlan: null,
            myPlanLoading: false,
            passwordForm: {
                current: '',
                new: '',
                confirm: ''
            },
            errorMsg: '',
            successMsg: '',
            mobileError: '',
            isSaving: false,
            errors: {},

            // OTP State
            otpSent: false,
            otpVerified: false,
            otpSending: false,
            otpVerifying: false,
            otpDigits: ['', '', '', '', '', ''],
            otpRefs: [],
            resendTimer: 0,
            resendInterval: null,
            otpAttempts: 0,
            maxOtpAttempts: 3,
            otpLocked: false,
            otpErrorType: null,
            resendCount: 0,
            maxResends: 3,

            // Country Code Picker
            countryCode: '91',
            ccDropdownOpen: false,
            ccSearch: '',
            countryCodes: [
        { code: '91', flag: '🇮🇳', name: 'India' },
        { code: '93', flag: '🇦🇫', name: 'Afghanistan' },
        { code: '355', flag: '🇦🇱', name: 'Albania' },
        { code: '213', flag: '🇩🇿', name: 'Algeria' },
        { code: '376', flag: '🇦🇩', name: 'Andorra' },
        { code: '244', flag: '🇦🇴', name: 'Angola' },
        { code: '54', flag: '🇦🇷', name: 'Argentina' },
        { code: '374', flag: '🇦🇲', name: 'Armenia' },
        { code: '61', flag: '🇦🇺', name: 'Australia' },
        { code: '43', flag: '🇦🇹', name: 'Austria' },
        { code: '994', flag: '🇦🇿', name: 'Azerbaijan' },
        { code: '973', flag: '🇧🇭', name: 'Bahrain' },
        { code: '880', flag: '🇧🇩', name: 'Bangladesh' },
        { code: '375', flag: '🇧🇾', name: 'Belarus' },
        { code: '32', flag: '🇧🇪', name: 'Belgium' },
        { code: '501', flag: '🇧🇿', name: 'Belize' },
        { code: '229', flag: '🇧🇯', name: 'Benin' },
        { code: '975', flag: '🇧🇹', name: 'Bhutan' },
        { code: '591', flag: '🇧🇴', name: 'Bolivia' },
        { code: '387', flag: '🇧🇦', name: 'Bosnia and Herzegovina' },
        { code: '267', flag: '🇧🇼', name: 'Botswana' },
        { code: '55', flag: '🇧🇷', name: 'Brazil' },
        { code: '673', flag: '🇧🇳', name: 'Brunei' },
        { code: '359', flag: '🇧🇬', name: 'Bulgaria' },
        { code: '226', flag: '🇧🇫', name: 'Burkina Faso' },
        { code: '257', flag: '🇧🇮', name: 'Burundi' },
        { code: '855', flag: '🇰🇭', name: 'Cambodia' },
        { code: '237', flag: '🇨🇲', name: 'Cameroon' },
        { code: '1', flag: '🇨🇦', name: 'Canada' },
        { code: '238', flag: '🇨🇻', name: 'Cape Verde' },
        { code: '236', flag: '🇨🇫', name: 'Central African Republic' },
        { code: '235', flag: '🇹🇩', name: 'Chad' },
        { code: '56', flag: '🇨🇱', name: 'Chile' },
        { code: '86', flag: '🇨🇳', name: 'China' },
        { code: '57', flag: '🇨🇴', name: 'Colombia' },
        { code: '269', flag: '🇰🇲', name: 'Comoros' },
        { code: '242', flag: '🇨🇬', name: 'Congo' },
        { code: '506', flag: '🇨🇷', name: 'Costa Rica' },
        { code: '385', flag: '🇭🇷', name: 'Croatia' },
        { code: '53', flag: '🇨🇺', name: 'Cuba' },
        { code: '357', flag: '🇨🇾', name: 'Cyprus' },
        { code: '420', flag: '🇨🇿', name: 'Czech Republic' },
        { code: '45', flag: '🇩🇰', name: 'Denmark' },
        { code: '253', flag: '🇩🇯', name: 'Djibouti' },
        { code: '593', flag: '🇪🇨', name: 'Ecuador' },
        { code: '20', flag: '🇪🇬', name: 'Egypt' },
        { code: '503', flag: '🇸🇻', name: 'El Salvador' },
        { code: '240', flag: '🇬🇶', name: 'Equatorial Guinea' },
        { code: '291', flag: '🇪🇷', name: 'Eritrea' },
        { code: '372', flag: '🇪🇪', name: 'Estonia' },
        { code: '251', flag: '🇪🇹', name: 'Ethiopia' },
        { code: '679', flag: '🇫🇯', name: 'Fiji' },
        { code: '358', flag: '🇫🇮', name: 'Finland' },
        { code: '33', flag: '🇫🇷', name: 'France' },
        { code: '241', flag: '🇬🇦', name: 'Gabon' },
        { code: '220', flag: '🇬🇲', name: 'Gambia' },
        { code: '995', flag: '🇬🇪', name: 'Georgia' },
        { code: '49', flag: '🇩🇪', name: 'Germany' },
        { code: '233', flag: '🇬🇭', name: 'Ghana' },
        { code: '30', flag: '🇬🇷', name: 'Greece' },
        { code: '502', flag: '🇬🇹', name: 'Guatemala' },
        { code: '224', flag: '🇬🇳', name: 'Guinea' },
        { code: '245', flag: '🇬🇼', name: 'Guinea-Bissau' },
        { code: '592', flag: '🇬🇾', name: 'Guyana' },
        { code: '509', flag: '🇭🇹', name: 'Haiti' },
        { code: '504', flag: '🇭🇳', name: 'Honduras' },
        { code: '36', flag: '🇭🇺', name: 'Hungary' },
        { code: '354', flag: '🇮🇸', name: 'Iceland' },
        { code: '62', flag: '🇮🇩', name: 'Indonesia' },
        { code: '98', flag: '🇮🇷', name: 'Iran' },
        { code: '964', flag: '🇮🇶', name: 'Iraq' },
        { code: '353', flag: '🇮🇪', name: 'Ireland' },
        { code: '972', flag: '🇮🇱', name: 'Israel' },
        { code: '39', flag: '🇮🇹', name: 'Italy' },
        { code: '225', flag: '🇨🇮', name: 'Ivory Coast' },
        { code: '1876', flag: '🇯🇲', name: 'Jamaica' },
        { code: '81', flag: '🇯🇵', name: 'Japan' },
        { code: '962', flag: '🇯🇴', name: 'Jordan' },
        { code: '7', flag: '🇰🇿', name: 'Kazakhstan' },
        { code: '254', flag: '🇰🇪', name: 'Kenya' },
        { code: '686', flag: '🇰🇮', name: 'Kiribati' },
        { code: '965', flag: '🇰🇼', name: 'Kuwait' },
        { code: '996', flag: '🇰🇬', name: 'Kyrgyzstan' },
        { code: '856', flag: '🇱🇦', name: 'Laos' },
        { code: '371', flag: '🇱🇻', name: 'Latvia' },
        { code: '961', flag: '🇱🇧', name: 'Lebanon' },
        { code: '266', flag: '🇱🇸', name: 'Lesotho' },
        { code: '231', flag: '🇱🇷', name: 'Liberia' },
        { code: '218', flag: '🇱🇾', name: 'Libya' },
        { code: '423', flag: '🇱🇮', name: 'Liechtenstein' },
        { code: '370', flag: '🇱🇹', name: 'Lithuania' },
        { code: '352', flag: '🇱🇺', name: 'Luxembourg' },
        { code: '261', flag: '🇲🇬', name: 'Madagascar' },
        { code: '265', flag: '🇲🇼', name: 'Malawi' },
        { code: '60', flag: '🇲🇾', name: 'Malaysia' },
        { code: '960', flag: '🇲🇻', name: 'Maldives' },
        { code: '223', flag: '🇲🇱', name: 'Mali' },
        { code: '356', flag: '🇲🇹', name: 'Malta' },
        { code: '222', flag: '🇲🇷', name: 'Mauritania' },
        { code: '230', flag: '🇲🇺', name: 'Mauritius' },
        { code: '52', flag: '🇲🇽', name: 'Mexico' },
        { code: '373', flag: '🇲🇩', name: 'Moldova' },
        { code: '976', flag: '🇲🇳', name: 'Mongolia' },
        { code: '382', flag: '🇲🇪', name: 'Montenegro' },
        { code: '212', flag: '🇲🇦', name: 'Morocco' },
        { code: '258', flag: '🇲🇿', name: 'Mozambique' },
        { code: '95', flag: '🇲🇲', name: 'Myanmar' },
        { code: '264', flag: '🇳🇦', name: 'Namibia' },
        { code: '977', flag: '🇳🇵', name: 'Nepal' },
        { code: '31', flag: '🇳🇱', name: 'Netherlands' },
        { code: '64', flag: '🇳🇿', name: 'New Zealand' },
        { code: '505', flag: '🇳🇮', name: 'Nicaragua' },
        { code: '227', flag: '🇳🇪', name: 'Niger' },
        { code: '234', flag: '🇳🇬', name: 'Nigeria' },
        { code: '47', flag: '🇳🇴', name: 'Norway' },
        { code: '968', flag: '🇴🇲', name: 'Oman' },
        { code: '92', flag: '🇵🇰', name: 'Pakistan' },
        { code: '507', flag: '🇵🇦', name: 'Panama' },
        { code: '675', flag: '🇵🇬', name: 'Papua New Guinea' },
        { code: '595', flag: '🇵🇾', name: 'Paraguay' },
        { code: '51', flag: '🇵🇪', name: 'Peru' },
        { code: '63', flag: '🇵🇭', name: 'Philippines' },
        { code: '48', flag: '🇵🇱', name: 'Poland' },
        { code: '351', flag: '🇵🇹', name: 'Portugal' },
        { code: '974', flag: '🇶🇦', name: 'Qatar' },
        { code: '40', flag: '🇷🇴', name: 'Romania' },
        { code: '7', flag: '🇷🇺', name: 'Russia' },
        { code: '250', flag: '🇷🇼', name: 'Rwanda' },
        { code: '966', flag: '🇸🇦', name: 'Saudi Arabia' },
        { code: '221', flag: '🇸🇳', name: 'Senegal' },
        { code: '381', flag: '🇷🇸', name: 'Serbia' },
        { code: '232', flag: '🇸🇱', name: 'Sierra Leone' },
        { code: '65', flag: '🇸🇬', name: 'Singapore' },
        { code: '421', flag: '🇸🇰', name: 'Slovakia' },
        { code: '386', flag: '🇸🇮', name: 'Slovenia' },
        { code: '252', flag: '🇸🇴', name: 'Somalia' },
        { code: '27', flag: '🇿🇦', name: 'South Africa' },
        { code: '82', flag: '🇰🇷', name: 'South Korea' },
        { code: '211', flag: '🇸🇸', name: 'South Sudan' },
        { code: '34', flag: '🇪🇸', name: 'Spain' },
        { code: '94', flag: '🇱🇰', name: 'Sri Lanka' },
        { code: '249', flag: '🇸🇩', name: 'Sudan' },
        { code: '597', flag: '🇸🇷', name: 'Suriname' },
        { code: '268', flag: '🇸🇿', name: 'Swaziland' },
        { code: '46', flag: '🇸🇪', name: 'Sweden' },
        { code: '41', flag: '🇨🇭', name: 'Switzerland' },
        { code: '963', flag: '🇸🇾', name: 'Syria' },
        { code: '886', flag: '🇹🇼', name: 'Taiwan' },
        { code: '992', flag: '🇹🇯', name: 'Tajikistan' },
        { code: '255', flag: '🇹🇿', name: 'Tanzania' },
        { code: '66', flag: '🇹🇭', name: 'Thailand' },
        { code: '228', flag: '🇹🇬', name: 'Togo' },
        { code: '676', flag: '🇹🇴', name: 'Tonga' },
        { code: '1868', flag: '🇹🇹', name: 'Trinidad and Tobago' },
        { code: '216', flag: '🇹🇳', name: 'Tunisia' },
        { code: '90', flag: '🇹🇷', name: 'Turkey' },
        { code: '993', flag: '🇹🇲', name: 'Turkmenistan' },
        { code: '256', flag: '🇺🇬', name: 'Uganda' },
        { code: '380', flag: '🇺🇦', name: 'Ukraine' },
        { code: '971', flag: '🇦🇪', name: 'United Arab Emirates' },
        { code: '44', flag: '🇬🇧', name: 'United Kingdom' },
        { code: '1', flag: '🇺🇸', name: 'United States' },
        { code: '598', flag: '🇺🇾', name: 'Uruguay' },
        { code: '998', flag: '🇺🇿', name: 'Uzbekistan' },
        { code: '678', flag: '🇻🇺', name: 'Vanuatu' },
        { code: '58', flag: '🇻🇪', name: 'Venezuela' },
        { code: '84', flag: '🇻🇳', name: 'Vietnam' },
        { code: '967', flag: '🇾🇪', name: 'Yemen' },
        { code: '260', flag: '🇿🇲', name: 'Zambia' },
        { code: '263', flag: '🇿🇼', name: 'Zimbabwe' },
            ],

            // Password Strength Data
            strengthScore: 0,
            strengthLabel: '',
            strengthColor: '#e5e7eb', // gray-200
            strengthPercent: 0,

            // Sidebar Config
            accountMenuItems: [
                {
                    id: 'profile',
                    label: 'My Profile',
                    // ... icon ...
                    action: () => {
                        this.activeTab = 'profile';
                        this.$router.push({ query: { tab: 'profile' } }); // URL update karega
                    }
                },
                {
                    id: 'subscription',
                    label: 'My Plan',
                    icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`,
                    action: () => {
                        this.activeTab = 'subscription';
                        this.$router.push({ query: { tab: 'subscription' } });
                        if (!this.myPlan) this.fetchMyPlan();
                    }
                },
                {
                    id: 'password',
                    label: 'Reset Password',
                    // ... icon ...
                    action: () => {
                        this.activeTab = 'password';
                        this.$router.push({ query: { tab: 'reset_password' } }); // URL update karega
                    }
                }
            ],
            backButtonConfig: {
                label: 'Back to Dashboard',
                icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>`,
                action: () => { this.$router.push({ name: 'BidListing' }); }
            }
        }
    },
    directives: {
        clickOutside: {
            mounted(el, binding) {
                el._clickOutsideHandler = (e) => { if (!el.contains(e.target)) binding.value(e); };
                document.addEventListener('click', el._clickOutsideHandler);
            },
            unmounted(el) {
                document.removeEventListener('click', el._clickOutsideHandler);
            }
        }
    },

    computed: {
        profileStrength() {
            let score = 0;
            if (this.userProfile.name) score += 30;
            if (this.userProfile.email) score += 35;
            if (this.otpVerified) score += 35;
            return score;
        },
        profileHint() {
            if (!this.userProfile.name) return "Add your full name to reach 100%";
            if (!this.otpVerified) return "Verify your mobile number via OTP to reach 100%";
            if (this.profileStrength === 100) return "Profile is fully complete!";
            return "Complete all fields to reach 100%";
        },
        selectedCountry() {
            return this.countryCodes.find(c => c.code === this.countryCode) || this.countryCodes[0];
        },
        filteredCountryCodes() {
            const q = this.ccSearch.trim().toLowerCase();
            if (!q) return this.countryCodes;
            return this.countryCodes.filter(c =>
                c.name.toLowerCase().includes(q) || c.code.includes(q)
            );
        },
        otpCode() {
            return this.otpDigits.join('');
        },
        planDurationLabel() {
            const days = this.myPlan?.duration_days;
            if (days === 30) return 'month';
            if (days === 90) return 'quarter';
            if (days === 365) return 'year';
            return days ? `${days} days` : '';
        },
        planStatusClass() {
            const s = this.myPlan?.plan_status;
            if (s === 'ACTIVE') return 'active';
            if (s === 'EXPIRING_SOON') return 'expiring';
            if (s === 'EXPIRED') return 'expired';
            return '';
        },
        planStatusLabel() {
            const s = this.myPlan?.plan_status;
            if (s === 'ACTIVE') return 'Active';
            if (s === 'EXPIRING_SOON') return 'Expiring Soon';
            if (s === 'EXPIRED') return 'Expired';
            return '—';
        },
        pdfUsagePct() {
            if (!this.myPlan || this.myPlan.max_pdfs_per_month === null || !this.myPlan.max_pdfs_per_month) return 0;
            return Math.min(100, Math.round((this.myPlan.pdfs_used_this_month / this.myPlan.max_pdfs_per_month) * 100));
        },
        licenseGeneratedPct() {
            if (!this.myPlan || this.myPlan.max_licenses === null || !this.myPlan.max_licenses) return 0;
            return Math.min(100, Math.round((this.myPlan.licenses_generated / this.myPlan.max_licenses) * 100));
        },
        licenseUsedPct() {
            if (!this.myPlan || !this.myPlan.licenses_generated) return 0;
            return Math.min(100, Math.round((this.myPlan.licenses_used / this.myPlan.licenses_generated) * 100));
        }
    },
    mounted() {
        this.fetchProfile();
        if (this.activeTab === 'subscription') this.fetchMyPlan();
    },

    beforeUnmount() {
        if (this.resendInterval) clearInterval(this.resendInterval);
    },
    methods: {
        formatPlanDate(dateStr) {
            if (!dateStr) return '—';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        },
        moduleLabel(value) {
            const labels = {
                tender_search: 'Tender Search & Bookmarks',
                bid_management: 'Bid Management (Bids, Projects, Knowledge Base)'
            };
            return labels[value] || value;
        },
        async fetchMyPlan() {
            this.myPlanLoading = true;
            try {
                const token = this.getAccessToken ? this.getAccessToken() : null;
                const storedInfo = localStorage.getItem('user-info') ? JSON.parse(localStorage.getItem('user-info')) : {};
                const actualToken = token || storedInfo.access_token;
                const response = await axios.get(this.$API_URL + '/user/my-plan', {
                    headers: { Authorization: 'Bearer ' + actualToken },
                    suppressErrorAlert: true
                });
                this.myPlan = response.data;
            } catch (e) {
                console.error('Failed to fetch plan summary', e);
            } finally {
                this.myPlanLoading = false;
            }
        },
        validateMobileInput() {
            // Remove non-numeric
            this.userProfile.mobile = this.userProfile.mobile.replace(/[^0-9]/g, '');

            if (this.userProfile.mobile.length !== 10) {
                this.mobileError = 'Mobile number must be exactly 10 digits';
            } else {
                this.mobileError = '';
            }
        },
        async safeUpdateProfile() {
            // Final validation
            this.validateMobileInput();
            if (this.mobileError || !this.userProfile.name) {
                return;
            }

            this.isSaving = true;
            try {
                const token = this.getAccessToken ? this.getAccessToken() : null;
                const storedInfo = localStorage.getItem('user-info') ? JSON.parse(localStorage.getItem('user-info')) : {};
                const actualToken = token || storedInfo.access_token;

                if (!actualToken) {
                    notify.warning('Your session has ended. Log in again to save your profile.');
                    this.$router.push({ name: 'LoginPage' });
                    return;
                }

                const response = await fetch(this.$API_URL + '/profile/update', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${actualToken}`
                    },
                    body: JSON.stringify({
                        name: this.userProfile.name,
                        mobile: this.userProfile.mobile
                    })
                });

                if (response.ok) {
                    const result = await response.json();
                    // Update Local Storage
                    const updatedInfo = { ...storedInfo, ...result.user };
                    localStorage.setItem('user-info', JSON.stringify(updatedInfo));

                    notify.success('Profile saved');
                } else {
                    const err = await response.json();
                    notify.error(typeof err.detail === 'string' && err.detail
                        ? err.detail
                        : 'Couldn’t save your profile. Check the fields and try again.');
                }

            } catch (error) {
                console.error(error);
                notify.error('Couldn’t save your profile. Check your connection and try again.');
            } finally {
                this.isSaving = false;
            }
        },
        handleSidebarClick(item) {
            // Actions are handled via the item.action callback, but we can hook here if needed
            if (!item.action && !item.route) {
                this.activeTab = item.id;
            }
        },
        async fetchProfile() {
            const stored = localStorage.getItem('user-info');
            let parsed = {};

            if (stored) {
                try {
                    parsed = JSON.parse(stored);
                } catch (e) {
                    console.error("Error parsing stored user info", e);
                }
            }

            // Initial load from storage
            this.userProfile = {
                name: parsed.name || parsed.username || '',
                email: parsed.email || '',
                mobile: parsed.mobile || ''
            };

            // Fetch profile from API to get latest data including verification status
            try {
                const token = this.getAccessToken ? this.getAccessToken() : null;
                const actualToken = token || (parsed && parsed.access_token);

                if (!actualToken) {
                    console.warn("No auth token available");
                    return;
                }

                const response = await axios.get(this.$API_URL + '/user/profile', {
                    headers: {
                        'Authorization': `Bearer ${actualToken}`
                    }
                });
                if (response.data) {
                    this.userProfile.name = response.data.name || this.userProfile.name;
                    this.userProfile.mobile = response.data.mobile || this.userProfile.mobile;
                    this.userProfile.email = response.data.email || this.userProfile.email;
                    this.otpVerified = response.data.is_phone_verified || false;
                }
            } catch (err) {
                console.error("Failed to fetch profile", err);
            }
        },
        validatePasswordStrength() {
            const password = this.passwordForm.new || '';
            let score = 0;

            if (password.length >= 8) score += 1;
            if (password.length >= 12) score += 1; // Bonus for length
            if (/[A-Z]/.test(password)) score += 1;
            if (/[0-9]/.test(password)) score += 1;
            if (/[^A-Za-z0-9]/.test(password)) score += 1;

            this.strengthScore = score;

            if (score <= 2) {
                this.strengthLabel = 'Weak';
                this.strengthColor = '#ef4444'; // Red
                this.strengthPercent = 33;
            } else if (score <= 4) {
                this.strengthLabel = 'Medium';
                this.strengthColor = '#eab308'; // Yellow
                this.strengthPercent = 66;
            } else {
                this.strengthLabel = 'Strong';
                this.strengthColor = '#22c55e'; // Green
                this.strengthPercent = 100;
            }
        },
        async handlePasswordReset() {
            this.errorMsg = "";
            this.successMsg = "";

            // Validate inputs
            if (!this.passwordForm.current) {
                this.errorMsg = "Current password is required";
                return;
            }
            if (!this.passwordForm.new || this.passwordForm.new.length < 6) {
                this.errorMsg = "New password must be at least 6 characters";
                return;
            }
            if (this.passwordForm.new !== this.passwordForm.confirm) {
                this.errorMsg = "New passwords do not match";
                return;
            }

            // Strength Check
            if (this.strengthScore < 3) {
                this.errorMsg = "Password is too weak. Please include numbers, symbols, and uppercase letters.";
                return;
            }

            try {
                const token = this.getAccessToken ? this.getAccessToken() : null;
                const storedInfo = localStorage.getItem('user-info') ? JSON.parse(localStorage.getItem('user-info')) : {};
                const actualToken = token || storedInfo.access_token;

                if (!actualToken) {
                    this.errorMsg = "Session expired. Please login again.";
                    return;
                }

                const response = await fetch(this.$API_URL + '/profile/reset-password', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${actualToken}`
                    },
                    body: JSON.stringify({
                        current_password: this.passwordForm.current,
                        new_password: this.passwordForm.new
                    })
                });

                const data = await response.json();

                if (response.ok) {
                    this.successMsg = "Password updated successfully";
                    this.passwordForm = { current: '', new: '', confirm: '' };
                } else {
                    // Check for 'message' (custom handler) or 'detail' (default FastAPI)
                    this.errorMsg = data.message || data.detail || "Failed to update password";
                }

            } catch (err) {
                console.error(err);
                this.errorMsg = "An error occurred while updating password.";
            }
        },
        toggleCountryDropdown() {
            this.ccDropdownOpen = !this.ccDropdownOpen;
            if (this.ccDropdownOpen) {
                this.ccSearch = '';
                this.$nextTick(() => this.$refs.ccSearchInput?.focus());
            }
        },
        closeCountryDropdown() {
            this.ccDropdownOpen = false;
        },
        selectCountry(c) {
            this.countryCode = c.code;
            this.ccDropdownOpen = false;
            this.resetOtpState();
        },
        resetOtpState() {
            this.otpSent = false;
            this.otpVerified = false;
            this.otpDigits = ['', '', '', '', '', ''];
            this.otpAttempts = 0;
            this.otpLocked = false;
            this.otpErrorType = null;
            if (this.resendInterval) clearInterval(this.resendInterval);
            this.resendTimer = 0;
            this.resendCount = 0;
            this.errors.otp = '';
        },
        startResendTimer() {
            this.resendTimer = 60;
            if (this.resendInterval) clearInterval(this.resendInterval);
            this.resendInterval = setInterval(() => {
                this.resendTimer--;
                if (this.resendTimer <= 0) clearInterval(this.resendInterval);
            }, 1000);
        },
        onOtpDigitInput(index, e) {
            const val = e.target.value.replace(/[^0-9]/g, '');
            this.otpDigits[index] = val.slice(-1);
            this.errors.otp = '';
            if (val && index < 5) {
                this.$nextTick(() => this.otpRefs[index + 1]?.focus());
            }
        },
        onOtpKeydown(index, e) {
            if (e.key === 'Backspace') {
                if (!this.otpDigits[index] && index > 0) {
                    this.otpDigits[index - 1] = '';
                    this.$nextTick(() => this.otpRefs[index - 1]?.focus());
                } else {
                    this.otpDigits[index] = '';
                }
            }
        },
        onOtpPaste(e) {
            e.preventDefault();
            const text = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 6);
            text.split('').forEach((d, i) => { this.otpDigits[i] = d; });
            const nextFocus = Math.min(text.length, 5);
            this.$nextTick(() => this.otpRefs[nextFocus]?.focus());
        },
        async sendOtp() {
            this.mobileError = '';
            if (this.userProfile.mobile.length < 7) {
                this.mobileError = "Enter a valid mobile number.";
                return;
            }
            this.otpSending = true;
            this.otpDigits = ['', '', '', '', '', ''];
            this.otpAttempts = 0;
            this.otpLocked = false;
            this.otpErrorType = null;
            try {
                await axios.post(this.$API_URL + "/whatsapp/send-registration-otp", {
                    phone_number: this.userProfile.mobile,
                    country_code: this.countryCode
                });
                this.otpSent = true;
                this.resendCount++;
                this.startResendTimer();
                this.$nextTick(() => this.otpRefs[0]?.focus());
            } catch (error) {
                const msg = error.response?.data?.detail || "Failed to send OTP. Try again.";
                const status = error.response?.status;
                if (status === 429) {
                    this.mobileError = "Too many OTP requests. Please wait 10 minutes.";
                } else {
                    this.mobileError = msg;
                }
            } finally {
                this.otpSending = false;
            }
        },
        async verifyOtp() {
            this.errors.otp = '';
            if (this.otpCode.length !== 6) {
                this.errors.otp = "Enter the 6-digit OTP from WhatsApp.";
                return;
            }
            this.otpVerifying = true;
            try {
                const token = this.getAccessToken ? this.getAccessToken() : null;
                const stored = localStorage.getItem('user-info');
                const parsed = stored ? JSON.parse(stored) : {};
                const actualToken = token || parsed.access_token;

                if (!actualToken) {
                    this.errors.otp = "Session expired. Please log in again.";
                    return;
                }

                await axios.post(this.$API_URL + "/user/verify-phone", {
                    mobile: this.userProfile.mobile,
                    country_code: this.countryCode,
                    otp_code: this.otpCode
                }, {
                    headers: {
                        'Authorization': `Bearer ${actualToken}`
                    }
                });
                this.otpVerified = true;
            } catch (error) {
                const msg = error.response?.data?.detail || "Invalid OTP. Please check and try again.";
                const attemptsMatch = msg.match(/(\d+)\s+attempt/);
                if (attemptsMatch) {
                    const remaining = parseInt(attemptsMatch[1]);
                    this.otpAttempts = this.maxOtpAttempts - remaining;
                    if (remaining === 0) {
                        this.otpLocked = true;
                        this.otpErrorType = 'max_attempts';
                    }
                }
                if (msg.includes("expired")) {
                    this.otpErrorType = 'expired';
                } else if (msg.includes("Maximum attempts")) {
                    this.otpLocked = true;
                    this.otpErrorType = 'max_attempts';
                }
                this.errors.otp = msg;
                this.otpDigits = ['', '', '', '', '', ''];
                if (!this.otpLocked) {
                    this.$nextTick(() => this.otpRefs[0]?.focus());
                }
            } finally {
                this.otpVerifying = false;
            }
        }
    }
}
</script>

<style scoped>
/* Page Specific Styles */
.account-section {
    padding: 1rem;
    max-width: 900px;
    width: 100%;
    margin: auto;
    /* Vertically center in the flex container */
}

.section-header {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    position: relative;
}

.section-header::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background: #0071C5;
    border-radius: 4px;
}

.section-title {
    font-size: var(--fs-h2);            /* 24px section title */
    line-height: var(--lh-h2);          /* 32px */
    font-weight: var(--fw-h2);          /* 600 */
    color: var(--text-primary);
    margin-bottom: var(--space-heading-body);
}

.section-subtitle {
    color: #64748b;
    font-size: 0.95rem;
}

/* Profile Card */
.profile-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.05);
    /* Premium shadow */
    overflow: hidden;
    border: 1px solid #f1f5f9;
    animation: slideUpFade 0.4s ease-out;
    /* Entry Animation */
}

@keyframes slideUpFade {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ===== My Plan / Subscription card ===== */
.plan-loading-state {
    padding: 3rem;
    text-align: center;
    color: #64748b;
    font-weight: 500;
}

.plan-empty-state {
    padding: 3rem 2rem;
    text-align: center;
    color: #64748b;
}

.plan-empty-state svg { opacity: 0.4; margin-bottom: 1rem; }

.plan-empty-state p {
    font-size: 1rem;
    font-weight: 600;
    color: #475569;
    margin: 0 0 0.4rem;
}

.plan-empty-hint {
    font-size: 0.85rem;
    color: #64748b;
}

.plan-card-account {
    padding: 2rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.plan-card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
}

.plan-name-account {
    margin: 0 0 0.4rem;
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
}

.plan-price-row {
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.plan-price-amount {
    font-size: 1.6rem;
    font-weight: 700;
    color: #0071C5;
}

.plan-price-period {
    font-size: 0.85rem;
    color: #64748b;
    font-weight: 500;
}

.plan-status-pill {
    padding: 5px 14px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
}

.plan-status-pill.active { background: rgba(16, 185, 129, 0.1); color: #10b981; }
.plan-status-pill.expiring { background: rgba(245, 158, 11, 0.1); color: #d97706; }
.plan-status-pill.expired { background: rgba(239, 68, 68, 0.1); color: #ef4444; }

.plan-expiry-line {
    margin: 0;
    font-size: 0.9rem;
    color: #64748b;
}

.plan-expiry-line strong { color: #0f172a; }

.plan-usage-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
    padding: 1.25rem;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #eef2f7;
}

.plan-usage-item { display: flex; flex-direction: column; gap: 0.5rem; }

.plan-usage-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.82rem;
    font-weight: 600;
    color: #475569;
}

.plan-usage-count {
    font-family: monospace;
    font-weight: 700;
    color: #0f172a;
}

.plan-usage-bar-bg {
    height: 8px;
    background: #e2e8f0;
    border-radius: 20px;
    overflow: hidden;
}

.plan-usage-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #0071C5, #0084FF);
    border-radius: 20px;
    transition: width 0.4s ease;
}

.plan-usage-bar-fill.warn {
    background: linear-gradient(90deg, #f59e0b, #ef4444);
}

.plan-usage-unlimited {
    font-size: 0.8rem;
    color: #10b981;
    font-weight: 600;
}

.plan-modules-row {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.plan-modules-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
}

.plan-modules-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.plan-module-chip {
    font-size: 0.78rem;
    font-weight: 700;
    padding: 5px 12px;
    border-radius: 20px;
    background: rgba(0, 113, 197, 0.08);
    color: #0071C5;
}

.plan-module-chip.none {
    background: #f1f5f9;
    color: #64748b;
}

@media (max-width: 780px) {
    .plan-usage-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 480px) {
    .plan-usage-grid { grid-template-columns: 1fr; }
}

.card-body {
    padding: 2rem;
    display: grid;
    grid-template-columns: 280px 1fr;
    /* Wider sidebar for avatar */
    gap: 4rem;
}

/* Split Card Layout for Reset Password */
.profile-card.split-layout {
    display: flex;
    flex-direction: row;
    min-height: 350px;
}

.split-visual {
    width: 40%;
    /* More vibrant, deep gradient */
    background: linear-gradient(135deg, #020617 0%, #1e3a8a 100%);
    position: relative;
    overflow: hidden;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    /* Center content */
    color: white;
    text-align: center;
}

.split-form {
    width: 60%;
    padding: 1.5rem 2rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

/* Visual Side Content */
.visual-content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.visual-icon-circle {
    width: 80px;
    /* Larger */
    height: 80px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    /* Softer corners */
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
    border: 1px solid rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(12px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2), 0 0 20px rgba(56, 189, 248, 0.3);
    /* Glow effect */
}

/* Icon inside color */
.visual-icon-circle svg {
    color: #7dd3fc;
    /* Light blue tint */
}

.split-visual h3 {
    font-size: var(--fs-h2);            /* 24px section title */
    line-height: var(--lh-h2);          /* 32px */
    font-weight: var(--fw-h2);          /* 600 */
    margin-bottom: 1rem;
    /* Gradient Text */
    background: linear-gradient(to right, #ffffff, #bae6fd);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.split-visual p {
    font-size: 1rem;
    color: #cbd5e1;
    line-height: 1.6;
    max-width: 80%;
    opacity: 0.9;
}

/* Decorative Circles */
.circle-1,
.circle-2 {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    /* Soften the blobs */
    z-index: 1;
}

.circle-1 {
    width: 300px;
    height: 300px;
    top: -50px;
    right: -50px;
    opacity: 0.6;
}

.circle-2 {
    width: 250px;
    height: 250px;
    bottom: -20px;
    left: -50px;
    opacity: 0.4;
}

/* Responsive Split */
@media (max-width: 900px) {
    .profile-card.split-layout {
        flex-direction: column;
    }

    .split-visual,
    .split-form {
        width: 100%;
        padding: 2rem;
    }

    .split-visual {
        min-height: 200px;
    }
}

/* Avatar Section */
.avatar-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding-right: 3rem;
    border-right: 1px solid #f1f5f9;
}

.avatar-wrapper {
    position: relative;
    width: 140px;
    /* Larger Avatar */
    height: 140px;
    margin-bottom: 1.5rem;
}

.avatar-image {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 4px solid white;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    /* More refined shadow */
    background: #f8fafc;
}

.avatar-upload-btn {
    position: absolute;
    bottom: 5px;
    right: 5px;
    background: #0071C5;
    color: white;
    width: 40px;
    /* Larger Button */
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 3px solid white;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 6px rgba(0, 113, 197, 0.3);
}

.avatar-upload-btn:hover {
    transform: scale(1.1);
    background: #005a9e;
}

.user-meta-name {
    font-weight: 700;
    font-size: 1.25rem;
    color: #1e293b;
    margin-bottom: 0.5rem;
    letter-spacing: -0.01em;
}

/* Avatar Status Indicator (Online Dot) */
.status-indicator {
    position: absolute;
    bottom: 15px;
    right: 15px;
    width: 14px;
    height: 14px;
    background: #10b981;
    /* Green */
    border: 2px solid white;
    border-radius: 50%;
    z-index: 2;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.meta-badges {
    display: flex;
    gap: 8px;
    justify-content: center;
    margin-bottom: 2rem;
}

.user-meta-id {
    color: #64748b;
    font-size: 0.8rem;
    padding: 6px 12px;
    background: #f1f5f9;
    border-radius: 20px;
    font-weight: 500;
}

/* Profile Strength Widget */
.profile-strength {
    width: 100%;
    text-align: left;
    background: #f8fafc;
    padding: 1.25rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.strength-header {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 0.75rem;
}

.strength-score {
    color: #0071C5;
}

.strength-bar-bg {
    width: 100%;
    height: 6px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 0.5rem;
}

.strength-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #0071C5, #00C6FF);
    border-radius: 4px;
    transition: width 1s ease-out;
}

.strength-hint {
    font-size: 0.75rem;
    color: #64748b;
}

/* Form Styles */
.form-grid {
    display: grid;
    gap: 1rem;
}

.form-group {
    margin-bottom: 0;
}

.form-group label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 0.35rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    text-align: left;
}

/* Verified Badge */
.label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: .2rem;
    /* Reduced to match label spacing */
}

.badge-verified {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #059669;
    /* Green */
    font-size: 0.75rem;
    font-weight: 600;
    background: #ecfdf5;
    padding: 2px 8px;
    border-radius: 12px;
}

/* Security Toggle Card */
.security-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem;
    background: #f0f9ff;
    /* Light Blue tint */
    border: 1px solid #bae6fd;
    border-radius: 12px;
    margin-top: 1rem;
}

.security-icon {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0071C5;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.security-info h4 {
    font-size: 0.95rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 2px 0;
}

.security-info p {
    color: #64748b;
    font-size: 0.9rem;
    margin: 0;
}

/* Password Strength Meter */
.strength-meter {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.strength-bar {
    flex-grow: 1;
    height: 6px;
    background-color: #f1f5f9;
    border-radius: 4px;
    overflow: hidden;
}

.strength-fill {
    height: 100%;
    transition: width 0.3s ease, background-color 0.3s ease;
}

.strength-text {
    font-size: 0.8rem;
    font-weight: 600;
    min-width: 50px;
    text-align: right;
}

/* Switch Toggle */
.switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
    margin-left: auto;
    /* Push to right */
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #cbd5e1;
    transition: .4s;
}

.slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .4s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

input:checked+.slider {
    background-color: #10b981;
    /* Green when active */
}

input:checked+.slider:before {
    transform: translateX(20px);
}

.slider.round {
    border-radius: 34px;
}

.slider.round:before {
    border-radius: 50%;
}


/* Input Wrapper & Icons */
.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 14px;
    color: #64748b;
    pointer-events: none;
    transition: color 0.2s;
}

.form-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    /* Softer radius */
    font-size: 0.95rem;
    color: #334155;
    transition: all 0.2s ease;
    background: #f8fafc;
}

.form-input.with-icon {
    padding-left: 44px;
    /* Space for icon */
}

.form-input[readonly] {
    background-color: #f1f5f9;
    color: #64748b;
    cursor: default;
    border-color: #e2e8f0;
}

.form-input[readonly]:focus {
    box-shadow: none;
    border-color: #e2e8f0;
}



/* Disabled Overlay Effect */
.disabled-overlay {
    position: relative;
    cursor: not-allowed;
}

.disabled-overlay::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(32, 31, 31, 0.247);
    /* Lightweight dark overlay as per user request */
    border-radius: 10px;
    z-index: 5;
    cursor: not-allowed;
}

.form-input:focus {
    background: white;
    border-color: #0071C5;
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1);
    /* Larger ring */
    outline: none;
}

.form-input:focus+.input-icon,
.input-wrapper:focus-within .input-icon {
    color: #0071C5;
    /* Icon lights up on focus */
}

.form-actions {
    margin-top: 1rem;
    display: flex;
    justify-content: flex-end;
    padding-top: 2rem;
    border-top: 1px solid #f1f5f9;
}

/* Button Styles */
.btn-primary {
    background: #0071C5;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 2px 4px rgba(0, 113, 197, 0.2);
}

.btn-primary:hover {
    background: #005a9e;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 113, 197, 0.3);
}

.btn-primary:active {
    transform: translateY(0);
}

/* Status Messages */
.status-messages {
    margin-top: 1rem;
}

.error-text,
.success-text {
    font-size: 0.9rem;
    padding: 10px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 0.5rem;
}

.error-text {
    color: #ef4444;
    background: #fee2e2;
}

.success-text {
    color: #10b981;
    background: #d1fae5;
}

/* Responsive */
/* Responsive Design */
@media (max-width: 1024px) {
    .card-body {
        grid-template-columns: 240px 1fr;
        gap: 2rem;
        padding: 2rem;
    }
}

@media (max-width: 900px) {

    /* Split Card Breakpoint */
    .profile-card.split-layout {
        flex-direction: column;
    }

    .split-visual,
    .split-form {
        width: 100%;
        padding: 2.5rem;
    }

    .split-visual {
        min-height: 220px;
        align-items: center;
        text-align: center;
    }

    .circle-1 {
        width: 200px;
        height: 200px;
        right: -20px;
    }
}

@media (max-width: 768px) {

    /* Main Layout Stack */
    .card-body {
        grid-template-columns: 1fr;
        gap: 2rem;
        padding: 1.5rem;
    }

    .account-section {
        padding: 1rem;
        margin-top: 1rem;
    }

    /* Avatar Section Mobile */
    .avatar-section {
        padding-right: 0;
        border-right: none;
        padding-bottom: 2rem;
        border-bottom: 1px solid #f1f5f9;
        margin-bottom: 0;
    }

    .profile-strength {
        max-width: 400px;
        /* Limit width on mobile */
        margin: 0 auto;
    }

    /* Form Tweaks */
    .form-grid {
        gap: 1.5rem;
    }

    .row-2-col {
        grid-template-columns: 1fr;
    }

    .section-title {
        font-size: var(--fs-h3);        /* 18px — shrinks on mobile */
        line-height: var(--lh-h3);
    }

    .form-actions {
        justify-content: center;
        /* Center buttons on mobile */
    }

    .btn-primary {
        width: 100%;
        justify-content: center;
    }

    /* Split Layout Mobile Tweaks */
    .split-form {
        padding: 2rem 1.5rem;
    }

    .visual-icon-circle {
        margin-bottom: 1rem;
    }

    .split-visual h3 {
        font-size: var(--fs-h3);        /* 18px — shrinks on mobile */
        line-height: var(--lh-h3);
    }
}

@media (max-width: 480px) {
    .section-header {
        margin-bottom: 1.5rem;
    }

    .profile-card {
        border-radius: 12px;
    }

    .card-body,
    .split-form,
    .split-visual {
        padding: 1.25rem;
    }

    .avatar-wrapper {
        width: 120px;
        height: 120px;
    }
}

/* Hide scrollbar for Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
    -ms-overflow-style: none;
    /* IE and Edge */
    scrollbar-width: none;
    /* Firefox */
}

/* OTP Components Styles */
.mobile-row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
}

.mobile-input-wrapper {
    position: relative;
    flex: 1;
    min-width: 0;
}

.mobile-input {
    width: 100%;
    height: 42px;
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.95rem;
    color: #1e293b;
    outline: none;
    transition: border-color 0.2s;
}

.mobile-input:focus {
    border-color: #0071C5;
}

.mobile-input:disabled {
    background: #f8fafc;
    color: #64748b;
    cursor: not-allowed;
}

.mobile-input.error-input {
    border-color: #ef4444;
}

.mobile-input.verified {
    padding-right: 48px;
}

.cc-picker {
    position: relative;
    flex-shrink: 0;
}

.cc-trigger {
    display: flex;
    align-items: center;
    gap: 5px;
    height: 42px;
    padding: 0 10px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    cursor: pointer;
    font-size: 0.88rem;
    color: #1e293b;
    white-space: nowrap;
    transition: border-color 0.2s, background 0.2s;
    min-width: 90px;
}

.cc-trigger:hover:not(:disabled) {
    border-color: #0071C5;
    background: #eff6ff;
}

.cc-trigger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.cc-flag {
    font-size: 1.2rem;
    line-height: 1;
}

.cc-code {
    font-weight: 600;
    font-size: 0.85rem;
}

.cc-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 999;
    width: 260px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
    overflow: hidden;
}

.cc-search-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid #f1f5f9;
    background: #f8fafc;
}

.cc-search {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.875rem;
    color: #1e293b;
}

.cc-search::placeholder {
    color: #64748b;
}

.cc-list {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    max-height: 250px;
    overflow-y: auto;
}

.cc-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    cursor: pointer;
    transition: background 0.15s;
    font-size: 0.875rem;
}

.cc-item:hover {
    background: #f0f9ff;
}

.cc-item.active {
    background: #eff6ff;
}

.cc-item-flag {
    font-size: 1.1rem;
    flex-shrink: 0;
}

.cc-item-name {
    flex: 1;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cc-item-code {
    color: #64748b;
    font-size: 0.8rem;
    flex-shrink: 0;
}

.cc-empty {
    padding: 16px;
    text-align: center;
    color: #64748b;
    font-size: 0.875rem;
}

.otp-send-btn {
    flex-shrink: 0;
    padding: 0 14px;
    height: 42px;
    background: #0071C5;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background 0.2s;
}

.otp-send-btn:disabled {
    background: #94a3b8;
    cursor: not-allowed;
}

.otp-send-btn:not(:disabled):hover {
    background: #005fa3;
}

.change-btn {
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    color: #0071C5;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s, background 0.2s;
}

.change-btn:hover {
    border-color: #0071C5;
    background: #eff6ff;
    color: #005fa3;
}

.verified-badge-circle {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 25px;
    height: 25px;
    border-radius: 50%;
    background: #25D366;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: white;
    box-shadow: 0 2px 8px rgba(37, 211, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.1);
}

.otp-hint {
    font-size: 0.82rem;
    color: #64748b;
    margin: 10px 0;
    text-align: left;
}

.otp-boxes-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.otp-box {
    width: 44px;
    height: 48px;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 700;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    outline: none;
    color: #1e293b;
    background: #f8fafc;
    transition: border-color 0.2s, background 0.2s;
    flex-shrink: 0;
}

.otp-box:focus {
    border-color: #0071C5;
    background: #eff6ff;
}

.otp-box:disabled {
    background: #f1f5f9;
    color: #64748b;
    cursor: not-allowed;
    opacity: 0.6;
}

.otp-box.error-input {
    border-color: #ef4444;
    background: #fff5f5;
}

.otp-box.error-input:disabled {
    border-color: #dc2626;
    background: #fee2e2;
}

.error-msg {
    font-size: 0.8rem;
    color: #dc2626;
    text-align: left;
}

.badge-pop-enter-active {
    animation: badge-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), badge-pulse 0.6s ease-out 0.5s;
}

.badge-pop-leave-active {
    animation: badge-pop 0.3s reverse;
}

@keyframes badge-pop {
    from {
        opacity: 0;
        transform: translateY(-50%) scale(0.3);
    }
    to {
        opacity: 1;
        transform: translateY(-50%) scale(1);
    }
}

@keyframes badge-pulse {
    0% {
        box-shadow: 0 3px 10px rgba(37, 211, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.1);
    }
    50% {
        box-shadow: 0 3px 10px rgba(37, 211, 102, 0.6), 0 0 0 6px rgba(37, 211, 102, 0.2);
    }
    100% {
        box-shadow: 0 3px 10px rgba(37, 211, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.1);
    }
}

.spinner-sm {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
